'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import AppLogo from '@/components/ui/AppLogo';
import { ArrowRight, ChevronDown, Shield, Star, Users, Car, Mail, MapPin, Award, TrendingUp, Zap, Globe, CheckCircle, Menu, X, ChevronRight, Wrench, Building2, Sparkles, Layers } from 'lucide-react';

// Auto-recover from ChunkLoadError (stale cache after deployment)
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    const msg = event?.message || '';
    if (msg?.includes('ChunkLoadError') || msg?.includes('Loading chunk') || msg?.includes('Failed to fetch dynamically imported module')) {
      const reloadKey = 'chunk_reload_attempted';
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, '1');
        window.location?.reload();
      }
    }
  });
}

const OnboardingFallback = () => (
  <div className="w-full h-screen bg-black flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-amber-400 text-sm">Loading…</p>
    </div>
  </div>
);

const SwipeableOnboarding = dynamic(() => import('@/components/SwipeableOnboarding'), {
  ssr: false,
  loading: () => <OnboardingFallback />,
});
const CompensationExplorer = dynamic(() => import('@/components/CompensationExplorer'), {
  ssr: false,
  loading: () => (
    <div className="w-full py-20 bg-zinc-900 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
    </div>
  ),
});

// ─── STATIC DATA ──────────────────────────────────────────────────────────────

const OUR_BUSINESSES = [
  {
    icon: Car,
    title: 'MBG Car Rentals & Chauffeur Services',
    desc: 'Professional vehicle rental and chauffeur services for individuals, businesses and events across South Africa.',
    tag: 'Mobility',
    color: 'text-amber-400',
    bg: 'bg-amber-400/10',
  },
  {
    icon: TrendingUp,
    title: 'MBG Car Sales',
    desc: 'Helping customers buy and sell quality vehicles through trusted partnerships and a transparent sales process.',
    tag: 'Automotive',
    color: 'text-blue-400',
    bg: 'bg-blue-400/10',
  },
  {
    icon: Sparkles,
    title: 'MBG Car Wash',
    desc: 'Professional vehicle cleaning and detailing services delivered with care and attention to quality.',
    tag: 'Services',
    color: 'text-green-400',
    bg: 'bg-green-400/10',
  },
  {
    icon: Wrench,
    title: 'MBG Tyres',
    desc: 'Quality tyres and automotive solutions to keep South African drivers safe and on the road.',
    tag: 'Automotive',
    color: 'text-purple-400',
    bg: 'bg-purple-400/10',
  },
  {
    icon: Building2,
    title: 'Future MBG Businesses',
    desc: 'MBG will continue expanding its business ecosystem to create additional value for members, customers and partners.',
    tag: 'Expanding',
    color: 'text-gold',
    bg: 'bg-gold/10',
  },
];

const PLATFORM_FEATURES = [
  {
    icon: Car,
    title: 'Premium Mobility Services',
    desc: 'Access to vehicle rentals and professional chauffeur services across South Africa at exclusive member rates.',
    tag: 'Services',
  },
  {
    icon: Shield,
    title: 'Enterprise Security',
    desc: 'Bank-grade encryption, role-based access control, multi-factor authentication and daily encrypted backups.',
    tag: 'Security',
  },
  {
    icon: Zap,
    title: 'Member Wallet',
    desc: 'Real-time wallet management. Track earnings, request withdrawals and manage your finances in one secure place.',
    tag: 'Finance',
  },
  {
    icon: Globe,
    title: 'Member Portal',
    desc: 'A dedicated digital workspace for every member — profile, banking, payments, notifications and activity history.',
    tag: 'Platform',
  },
  {
    icon: TrendingUp,
    title: 'Business Opportunities',
    desc: 'Access MBG business services and participate in the MBG ecosystem as it grows across South Africa.',
    tag: 'Growth',
  },
  {
    icon: Award,
    title: 'MBG Rewards Programme',
    desc: 'Members who participate in accordance with the MBG Compensation Plan may progress through structured reward levels.',
    tag: 'Rewards',
  },
];

const MEMBERSHIP_LEVELS = [
  { name: 'Elite 1000', amount: 'R1,000', desc: 'Entry-level participation in the MBG Stokvel' },
  { name: 'Elite 2000', amount: 'R2,000', desc: 'Expanded access and progression opportunities' },
  { name: 'Elite 4000', amount: 'R4,000', desc: 'Mid-tier participation level' },
  { name: 'Elite 8000', amount: 'R8,000', desc: 'Advanced membership level' },
  { name: 'Elite 16000', amount: 'R16,000', desc: 'Senior participation tier' },
  { name: 'Elite 32000+', amount: 'R32,000+', desc: 'Premium membership levels' },
];

// ─── ECOSYSTEM HIERARCHY ──────────────────────────────────────────────────────
const ECOSYSTEM_LAYERS = [
  { label: 'MBG', desc: 'South African business group — the parent organisation', icon: Building2, level: 0 },
  { label: 'MBG Nexus', desc: 'The secure digital platform connecting all MBG services and members', icon: Layers, level: 1 },
  { label: 'MBG Stokvel', desc: 'The primary membership programme — structured participation and community growth', icon: Users, level: 2 },
  { label: 'MBG Rewards Programme', desc: 'Structured reward milestones earned through the MBG Compensation Plan', icon: Award, level: 3 },
  { label: 'MBG Business Ecosystem', desc: 'Car rentals, car sales, car wash, tyres and future business verticals', icon: Car, level: 4 },
];

// ─── HOMEPAGE ─────────────────────────────────────────────────────────────────
export default function HomePage() {
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* ── NAV ── */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-background/95 backdrop-blur-xl border-b border-border shadow-2xl' : 'bg-black/30 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-18 flex items-center justify-between py-4">
          <Link href="/" className="flex items-center gap-3">
            <AppLogo size={38} />
            <div>
              <span className="font-bold text-lg text-foreground tracking-tight block leading-none">MBG Nexus</span>
              <span className="text-[10px] text-gold uppercase tracking-widest">MBG Stokvel Platform</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {[
              { label: 'About', href: '#about' },
              { label: 'The Stokvel', href: '#stokvel' },
              { label: 'Our Businesses', href: '#businesses' },
              { label: 'Platform', href: '#platform' },
              { label: 'Membership', href: '#membership' },
              { label: 'Contact', href: '#contact' },
            ]?.map(item => (
              <a key={item?.label} href={item?.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 font-medium">
                {item?.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link href="/sign-up-login-screen" className="btn-outline-gold text-sm px-5 py-2.5">
              Sign In
            </Link>
            <Link href="/sign-up-login-screen" className="btn-gold text-sm px-5 py-2.5">
              Join MBG <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <button onClick={() => setNavOpen(!navOpen)} className="lg:hidden p-2 text-muted-foreground hover:text-foreground">
            {navOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {navOpen && (
          <div className="lg:hidden bg-surface-1 border-b border-border px-6 py-4 space-y-3">
            {[
              { label: 'About', href: '#about' },
              { label: 'The Stokvel', href: '#stokvel' },
              { label: 'Our Businesses', href: '#businesses' },
              { label: 'Platform', href: '#platform' },
              { label: 'Membership', href: '#membership' },
              { label: 'Contact', href: '#contact' },
            ]?.map(item => (
              <a key={item?.label} href={item?.href} onClick={() => setNavOpen(false)} className="block text-sm text-muted-foreground hover:text-foreground py-2 font-medium">
                {item?.label}
              </a>
            ))}
            <div className="pt-3 flex flex-col gap-2">
              <Link href="/sign-up-login-screen" className="btn-outline-gold text-sm text-center">Sign In</Link>
              <Link href="/sign-up-login-screen" className="btn-gold text-sm text-center">Join the MBG Stokvel</Link>
            </div>
          </div>
        )}
      </header>

      {/* ── HERO (SWIPEABLE ONBOARDING) ── */}
      <SwipeableOnboarding plans={[]} />

      {/* ── SCROLL INDICATOR ── */}
      <div className="flex flex-col items-center gap-2 py-6 bg-background animate-bounce">
        <span className="text-xs text-muted-foreground uppercase tracking-widest">Discover MBG</span>
        <ChevronDown className="w-5 h-5 text-gold" />
      </div>

      {/* ── ABOUT MBG ── */}
      <section id="about" className="py-28 relative overflow-hidden">
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gold/3 rounded-full blur-[100px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-semibold text-gold uppercase tracking-widest mb-4">About MBG</p>
              <h2 className="text-hero-md mb-6 text-foreground">
                Building Businesses. Creating Opportunities.
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6 text-lg">
                MBG is a South African business group founded in Durban, KwaZulu-Natal. Built on a foundation of innovation, transparency and long-term sustainable growth, MBG operates across the premium mobility sector and continues to expand its ecosystem of services.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                <strong className="text-foreground">MBG Nexus</strong> is the secure digital platform that connects the MBG ecosystem — bringing together members, customers, business partners and the MBG team within one secure digital environment. Through MBG Nexus, members can manage their membership, participation, progress, rewards, payments and all related MBG services.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-10">
                The <strong className="text-foreground">MBG Stokvel</strong> is the primary membership programme offered through MBG Nexus. The MBG Rewards Programme is one component of this broader ecosystem. Members who participate in accordance with the MBG Compensation Plan and Programme Rules may progress through the programme while accessing MBG products, services and future business opportunities.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  'South African Business Group',
                  'Founded in Durban, KwaZulu-Natal',
                  'Premium mobility services',
                  'Driven by innovation',
                  'Committed to transparency',
                  'Long-term sustainable growth',
                  'Finance-verified payment pipeline',
                  'Enterprise-grade data security',
                ]?.map(item => (
                  <div key={item} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bento grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="glass-card rounded-3xl p-6 col-span-2 flex items-center gap-5">
                <div className="w-14 h-14 rounded-2xl gold-gradient flex items-center justify-center shrink-0">
                  <Building2 className="w-7 h-7 text-primary-foreground" />
                </div>
                <div>
                  <p className="font-bold text-foreground text-lg">MBG Business Ecosystem</p>
                  <p className="text-muted-foreground text-sm">A growing South African business group connecting mobility services, member opportunities and technology through one platform</p>
                </div>
              </div>
              <div className="glass-card rounded-3xl p-6">
                <Users className="w-8 h-8 text-gold mb-3" />
                <p className="font-bold text-foreground mb-1">MBG Stokvel</p>
                <p className="text-sm text-muted-foreground">Structured membership programme</p>
              </div>
              <div className="glass-card rounded-3xl p-6">
                <Shield className="w-8 h-8 text-gold mb-3" />
                <p className="font-bold text-foreground mb-1">Enterprise Security</p>
                <p className="text-sm text-muted-foreground">Bank-grade protection</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MBG STOKVEL SECTION ── */}
      <section id="stokvel" className="py-28 bg-surface-1">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold text-gold uppercase tracking-widest mb-4">Primary Membership Programme</p>
            <h2 className="text-hero-md text-foreground mb-4">The MBG Stokvel</h2>
            <p className="text-muted-foreground max-w-3xl mx-auto text-lg">
              The MBG Stokvel is a structured community membership programme. Members join at a chosen participation level, contribute through the EFT payment process, and participate in the MBG binary referral network. As the community grows and members progress through the programme, rewards are earned in accordance with the MBG Compensation Plan.
            </p>
          </div>

          {/* How it works */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              { step: '01', title: 'Register', desc: 'Create your MBG Nexus account and choose your Elite participation level.', icon: Users },
              { step: '02', title: 'Contribute', desc: 'Make your EFT payment and upload proof of payment through MBG Nexus.', icon: Zap },
              { step: '03', title: 'Participate', desc: 'Your account is activated. Build your binary referral network and track your progress.', icon: TrendingUp },
              { step: '04', title: 'Progress', desc: 'As your tree grows and milestones are reached, rewards are credited to your wallet.', icon: Award },
            ]?.map(item => (
              <div key={item?.step} className="glass-card rounded-3xl p-6 text-center group hover:border-gold/30 transition-all duration-300">
                <div className="w-12 h-12 rounded-2xl gold-gradient flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <item.icon className="w-6 h-6 text-primary-foreground" />
                </div>
                <p className="text-xs font-bold text-gold mb-2 tracking-widest">{item?.step}</p>
                <h3 className="font-bold text-foreground mb-2">{item?.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item?.desc}</p>
              </div>
            ))}
          </div>

          {/* Ecosystem hierarchy */}
          <div className="max-w-3xl mx-auto">
            <p className="text-center text-xs font-semibold text-gold uppercase tracking-widest mb-8">The MBG Ecosystem Structure</p>
            <div className="space-y-3">
              {ECOSYSTEM_LAYERS?.map((layer, i) => (
                <div key={layer?.label} className="flex items-center gap-4 glass-card rounded-2xl p-4 hover:border-gold/30 transition-all duration-300"
                  style={{ marginLeft: `${layer?.level * 20}px` }}>
                  <div className="w-10 h-10 rounded-xl gold-gradient flex items-center justify-center shrink-0">
                    <layer.icon className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-foreground text-sm">{layer?.label}</p>
                    <p className="text-xs text-muted-foreground">{layer?.desc}</p>
                  </div>
                  {i < ECOSYSTEM_LAYERS?.length - 1 && (
                    <ChevronRight className="w-4 h-4 text-gold/40 shrink-0" />
                  )}
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-muted-foreground mt-6">
              MBG is the parent organisation. MBG Nexus is the digital platform. The MBG Stokvel is the membership programme. The MBG Rewards Programme operates within the Stokvel. The MBG Business Ecosystem is separate from the Stokvel itself.
            </p>
          </div>
        </div>
      </section>

      {/* ── OUR BUSINESSES ── */}
      <section id="businesses" className="py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold text-gold uppercase tracking-widest mb-4">The MBG Ecosystem</p>
            <h2 className="text-hero-md text-foreground mb-4">Our Businesses</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              MBG operates across multiple business verticals, creating real value for customers and members across South Africa. These businesses are separate from the MBG Stokvel membership programme.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {OUR_BUSINESSES?.map(biz => (
              <div key={biz?.title} className="glass-card rounded-3xl p-7 group hover:border-gold/30 transition-all duration-300">
                <div className="flex items-start justify-between mb-5">
                  <div className={`w-12 h-12 rounded-2xl ${biz?.bg} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                    <biz.icon className={`w-6 h-6 ${biz?.color}`} />
                  </div>
                  <span className="text-xs font-semibold text-gold bg-gold/10 px-2.5 py-1 rounded-full">{biz?.tag}</span>
                </div>
                <h3 className="text-base font-bold text-foreground mb-2">{biz?.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{biz?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PLATFORM FEATURES ── */}
      <section id="platform" className="py-28 bg-surface-1">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold text-gold uppercase tracking-widest mb-4">MBG Nexus Platform</p>
            <h2 className="text-hero-md text-foreground mb-4">One platform. Every service.</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              MBG Nexus brings together all MBG services, member management, financial workflows and business tools within a single secure platform.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PLATFORM_FEATURES?.map(feature => (
              <div key={feature?.title} className="glass-card rounded-3xl p-7 group hover:border-gold/30 transition-all duration-300">
                <div className="flex items-start justify-between mb-5">
                  <div className="w-12 h-12 rounded-2xl gold-gradient flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <feature.icon className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <span className="text-xs font-semibold text-gold bg-gold/10 px-2.5 py-1 rounded-full">{feature?.tag}</span>
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">{feature?.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{feature?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPENSATION EXPLORER ── */}
      <CompensationExplorer />

      {/* ── MEMBERSHIP LEVELS ── */}
      <section id="membership" className="py-28 bg-surface-1">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold text-gold uppercase tracking-widest mb-4">MBG Stokvel Participation Levels</p>
            <h2 className="text-hero-md text-foreground mb-4">Choose Your Level</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              The MBG Stokvel offers structured participation levels. Members choose their entry level and progress in accordance with the MBG Compensation Plan and Programme Rules. No income is guaranteed.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {MEMBERSHIP_LEVELS?.map(level => (
              <div key={level?.name} className="glass-card rounded-2xl p-5 hover:border-gold/30 transition-all">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-bold text-foreground">{level?.name}</p>
                  <span className="text-gold font-bold text-sm">{level?.amount}</span>
                </div>
                <p className="text-xs text-muted-foreground">{level?.desc}</p>
              </div>
            ))}
          </div>
          <div className="glass-card rounded-3xl p-6 border border-amber-400/20 bg-amber-400/5">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-foreground mb-1">Programme Disclosure</p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Participation in the MBG Stokvel and MBG Rewards Programme does not guarantee income. Rewards are earned in accordance with the MBG Compensation Plan. Members are encouraged to read the full Programme Rules before participating. MBG is committed to transparency, compliance and long-term sustainable business practices.
                </p>
              </div>
            </div>
          </div>
          <div className="text-center mt-8">
            <a href="#compensation-explorer" className="btn-gold text-base px-10 py-4 rounded-2xl inline-flex items-center gap-2">
              View Compensation Plan <ChevronRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* ── MEMBER STORIES PLACEHOLDER ── */}
      <section className="py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-gold uppercase tracking-widest mb-4">Member Stories</p>
            <h2 className="text-hero-md text-foreground mb-4">Verified Member Experiences</h2>
          </div>
          <div className="glass-card rounded-3xl p-10 text-center max-w-2xl mx-auto border border-gold/20">
            <div className="w-16 h-16 rounded-2xl gold-gradient flex items-center justify-center mx-auto mb-6">
              <Star className="w-8 h-8 text-primary-foreground" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-4">Coming Soon</h3>
            <p className="text-muted-foreground leading-relaxed">
              Verified member success stories will be published here after launch. MBG is committed to sharing only genuine, verified experiences from real members. We do not publish fictional testimonials.
            </p>
            <p className="text-sm text-gold mt-4 font-medium">Authentic stories. Real members. Verified results.</p>
          </div>
        </div>
      </section>

      {/* ── MBG SMART PLACEMENT™ FAQ ── */}
      <section className="py-20 bg-surface-1">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-gold uppercase tracking-widest mb-4">Community Growth</p>
            <h2 className="text-hero-md text-foreground mb-4">MBG Smart Placement™</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Understanding MBG Nexus' community-assisted placement system within the MBG Stokvel.
            </p>
          </div>
          <div className="space-y-4">
            <div className="glass-card rounded-2xl p-6 border border-gold/20">
              <h3 className="font-bold text-foreground mb-3 flex items-center gap-2">
                <Zap className="w-4 h-4 text-gold shrink-0" />
                What is MBG Smart Placement™?
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                MBG Smart Placement™ is MBG Nexus' community-assisted placement system within the MBG Stokvel. Members who actively contribute to growing the community may become eligible to receive community-assisted placements in accordance with the MBG Programme Rules. The system is designed to encourage fairness, transparency and sustainable growth throughout the community.
              </p>
              <p className="text-sm text-gold italic mt-3">
                "Earn your place. Grow your community. Let the community help you grow."
              </p>
            </div>
            <div className="glass-card rounded-2xl p-6 border border-gold/20">
              <h3 className="font-bold text-foreground mb-3">How do I qualify for MBG Smart Placement™?</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Once your MBG Stokvel membership is activated, you begin a qualification window. During this period, recruit a minimum number of verified active members to become eligible. The system considers your participation, fairness and overall network balance when determining eligibility.
              </p>
            </div>
            <div className="glass-card rounded-2xl p-6 border border-gold/20">
              <h3 className="font-bold text-foreground mb-3">Is the placement algorithm public?</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                To protect the integrity of the programme, the internal placement methodology is confidential and is continuously monitored for fairness and consistency. Members can always view their current qualification progress, remaining requirements, qualification status and placement status — without the internal algorithm being exposed.
              </p>
            </div>
            <div className="glass-card rounded-2xl p-6 border border-gold/20">
              <h3 className="font-bold text-foreground mb-3">What if I miss the qualification window?</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Your account, wallet and existing referrals remain fully active. You can still qualify for MBG Smart Placement™ at any time by completing the required verified active recruits — even after the original qualification period has passed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTACT / CTA ── */}
      <section id="contact" className="py-28 bg-surface-1">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <p className="text-xs font-semibold text-gold uppercase tracking-widest mb-4">Join the MBG Stokvel</p>
          <h2 className="text-hero-md text-foreground mb-6">Become part of the MBG community</h2>
          <p className="text-muted-foreground mb-10 text-lg max-w-2xl mx-auto">
            Join a growing South African community built on innovation, transparency and opportunity. Access premium services, connect with a national network and participate in the MBG Stokvel through MBG Nexus.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/sign-up-login-screen" className="btn-gold text-base px-10 py-4 rounded-2xl flex items-center gap-2">
              Register Now <ArrowRight className="w-5 h-5" />
            </Link>
            <a href="mailto:mbgnexus@gmail.com"
              className="btn-outline-gold text-base px-10 py-4 rounded-2xl flex items-center gap-2">
              <Mail className="w-5 h-5" /> Contact Support
            </a>
          </div>
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-8 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gold" />
              <a href="mailto:mbgnexus@gmail.com" className="hover:text-foreground transition-colors">mbgnexus@gmail.com</a>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gold" />
              <span>Durban, KwaZulu-Natal, South Africa</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-10 border-t border-border bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AppLogo size={28} />
            <div>
              <span className="font-bold text-foreground block">MBG Nexus</span>
              <span className="text-[10px] text-muted-foreground">Digital Platform for the MBG Stokvel</span>
            </div>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              © {new Date()?.getFullYear()} MBG Group. All rights reserved. Durban, KwaZulu-Natal, South Africa.
            </p>
            <p className="text-[10px] text-muted-foreground/60 mt-1 max-w-lg">
              MBG Nexus is the digital platform for the MBG Stokvel. Participation in the MBG Stokvel and MBG Rewards Programme does not guarantee income. Members are encouraged to read the full Programme Rules before participating.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Link href="/sign-up-login-screen" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Sign In</Link>
            <Link href="/sign-up-login-screen" className="text-xs text-gold hover:text-gold-light transition-colors">Register</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}